"use client";

// Reps : l'inventaire sur téléphone (clés + boosters), en un tap.
//
// Pourquoi un bouton flottant plutôt qu'une carte dans la page : sous lg, le
// rail droit n'existe pas, et aucune place du flux n'est atteignable. Le
// parcours fait des dizaines d'écrans ET s'ouvre centré sur la leçon courante
// (RepsPathClient) : posé après lui, le rail était à des dizaines d'écrans vers
// le bas ; posé avant lui, il l'est vers le haut dès qu'on a un peu progressé.
// Un bouton fixe suit le pouce où qu'on soit dans le parcours.
//
// Le solde de clés se lit SUR le bouton, sans l'ouvrir : une ressource rare ne
// pèse que si on la voit fondre (et c'est l'info qu'on vient chercher 9 fois
// sur 10). "·" tant que l'état n'est pas connu, comme la capsule du header
// (components/ui/Gamification/Keys/Keys.js) : on ne montre jamais un solde
// inventé.
//
// Le bouton se pose à GAUCHE : celui de recentrage du parcours occupe déjà le
// coin droit, et il apparaît/disparaît au scroll (un empilement ferait sauter
// celui-ci à chaque fois).

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useSession } from "next-auth/react";
import Image from "next/image";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import RepsKeyCard from "@/components/Reps/RepsKeyCard";
import RepsBoosterCard from "@/components/Reps/RepsBoosterCard";
import { hasUnlimitedEnergy } from "@/lib/reps/access";

export default function RepsInventoryFab() {
    const t = useTranslations();
    const { data: session } = useSession();
    const [open, setOpen] = useState(false);
    const [state, setState] = useState(null);

    // Même source que la carte Clés, et mêmes déclencheurs de rafraîchissement
    // (retour sur l'onglet, clé dépensée ou rachetée ailleurs dans l'app).
    useEffect(() => {
        let cancelled = false;
        function load() {
            fetch("/api/reps/keys-state", { method: "POST" })
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => {
                    if (!cancelled && d && d.keys) setState(d.keys);
                })
                .catch(() => {});
        }
        load();
        const onRefresh = () => load();
        window.addEventListener("focus", onRefresh);
        window.addEventListener("reps:keys-changed", onRefresh);
        return () => {
            cancelled = true;
            window.removeEventListener("focus", onRefresh);
            window.removeEventListener("reps:keys-changed", onRefresh);
        };
    }, []);

    const unlimited = state
        ? state.unlimited
        : hasUnlimitedEnergy(session && session.user);
    // Exactement le nombre qu'affiche RepsKeyCard, et pas un total recalculé :
    // le bouton et la carte qu'il ouvre ne doivent jamais annoncer deux soldes
    // différents (la carte détaille bonus et clés de démarrage à part).
    const count = state && !state.unlimited ? state.keys : 0;

    return (
        <>
            {/* lg:hidden : sur ordinateur, le rail de droite montre déjà tout. */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label={t("reps_inventory_open")}
                className="fixed bottom-6 left-4 z-40 flex h-12 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 shadow-[0_4px_10px_rgba(0,0,0,0.12)] transition-transform hover:scale-105 dark:border-white/10 dark:bg-bgDark-840 lg:hidden"
            >
                <Image
                    src="/static/icons/key.svg"
                    width={16}
                    height={16}
                    alt=""
                />
                {!state ? (
                    <span className="text-sm font-bold text-gray-300 dark:text-grayDark-600">
                        ·
                    </span>
                ) : unlimited ? (
                    <SolarIcon
                        name="infinity-bold"
                        className="text-base text-amber-600 dark:text-amber-400"
                    />
                ) : (
                    <span className="text-sm font-bold tabular-nums text-amber-600 dark:text-amber-400">
                        {count}
                    </span>
                )}
            </button>

            <BottomSheetModal isOpen={open} onClose={() => setOpen(false)}>
                <div className="p-5">
                    <div className="mb-4 flex items-center justify-between">
                        <p className="mb-0 text-base font-bold text-gray-900 dark:text-white">
                            {t("reps_inventory_title")}
                        </p>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label={t("reps_inventory_close")}
                            className="-mr-1 grid h-8 w-8 place-items-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-grayDark-200"
                        >
                            <SolarIcon
                                name="close-circle-linear"
                                className="text-xl"
                            />
                        </button>
                    </div>

                    {/* Les cartes du rail, telles quelles : une seule
                        implémentation des clés et des boosters à maintenir. */}
                    <div className="space-y-4">
                        <RepsKeyCard />
                        <RepsBoosterCard />
                    </div>
                </div>
            </BottomSheetModal>
        </>
    );
}
